import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { CheckIcon } from 'lucide-react'
import { Label, Pie, PieChart } from 'recharts'

import { queryUserSubmissionProgress } from '@/services/user/query'

import { Button } from '@/components/ui/button'
import { ChartConfig, ChartContainer } from '@/components/ui/chart'

import { cn } from '@/lib/cn'

export const Route = createFileRoute('/dashboard/')({
  component: RouteComponent,
})

function RouteComponent() {
  return (
    <div className="flex flex-col gap-6">
      <OnBoardingBanner />
      <div className="grid gap-4 md:grid-cols-2">
        <ProfileSubmissionProgress />
        <ProfileSubmissionProgressChart />
      </div>
    </div>
  )
}

function OnBoardingBanner() {
  return (
    <div className="space-y-6 rounded-lg bg-yes-600 p-6 text-white">
      <h1 className="text-2xl font-bold">Selamat Datang!</h1>
      <p className="max-w-lg">
        Wujudkan karier impian Anda bersama YES.co.id, platform terpercaya untuk
        menemukan peluang kerja yang tepat dan sesuai dengan potensi Anda!
      </p>
      <Button variant="secondary" className="text-yes-600" asChild>
        <Link to="/" hash="job-listing">
          Cari Lowongan
        </Link>
      </Button>
    </div>
  )
}

function ProfileSubmissionProgress() {
  const progressQuery = useSuspenseQuery({
    ...queryUserSubmissionProgress(true),
    select: (data) => data!,
  })

  const data = {
    'Informasi Pribadi': {
      completed: progressQuery.data.user_personal_info,
      to: '/profile/personal',
    },
    Bahasa: {
      completed: progressQuery.data.user_language,
      to: '/profile/languages',
    },
    Pendidikan: {
      completed: progressQuery.data.user_education,
      to: '/profile/education',
    },
    'Riwayat Pekerjaan': {
      completed: progressQuery.data.user_experience,
      to: '/profile/experience',
    },
    'Lisensi / Sertifikat': {
      completed: progressQuery.data.user_attachment,
      to: '/profile/licences',
    },
  }

  return (
    <div className="rounded-lg bg-white p-6 shadow-sm">
      <h2 className="text-lg font-semibold">Status Kelengkapan Profil</h2>
      <p className="mt-0.5 text-sm">
        Lengkapi profilmu agar dapat melamar pekerjaan!
      </p>
      <div className="mt-3 flex flex-col gap-3">
        {Object.entries(data).map(([label, { completed, to }]) => (
          <div key={label} className="flex items-center gap-2">
            <div className="w-6">
              <div
                className={cn(
                  'flex size-5 items-center justify-center rounded-full',
                  completed ? 'bg-green-400' : 'bg-gray-400'
                )}
              >
                <CheckIcon size={16} strokeWidth={2} className="text-white" />
              </div>
            </div>
            <p className="grow">{label}</p>
            {!completed && (
              <Link
                to={to}
                className="text-blue-600 underline underline-offset-2"
              >
                Lengkapi
              </Link>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

function ProfileSubmissionProgressChart() {
  const completed = useSuspenseQuery({
    ...queryUserSubmissionProgress(true),
    select(data) {
      if (!data) return 0
      const {
        user_personal_info,
        user_language,
        user_education,
        user_experience,
        user_attachment,
      } = data

      return (
        ([
          user_personal_info,
          user_language,
          user_education,
          user_experience,
          user_attachment,
        ].reduce((acc, cur) => acc + (cur ? 1 : 0), 0) *
          100) /
        5
      )
    },
  })
  const chartData = [
    {
      status: 'completed',
      value: completed.data,
      fill: 'var(--color-completed)',
    },
    {
      status: 'notCompleted',
      value: 100 - completed.data,
      fill: 'var(--color-notCompleted)',
    },
  ]

  const chartConfig = {
    completed: {
      label: 'Completed',
      color: 'rgb(74 222 128)',
    },
    notCompleted: {
      label: 'Not Completed',
      color: 'rgb(239 68 68)',
    },
  } satisfies ChartConfig

  return (
    <div className="rounded-lg bg-white p-6 shadow-sm">
      <h2 className="text-lg font-semibold">Status Kelengkapan Profil</h2>
      <ChartContainer
        config={chartConfig}
        className="mx-auto aspect-square max-h-[200px]"
      >
        <PieChart>
          <Pie
            data={chartData}
            dataKey="value"
            nameKey="status"
            innerRadius={60}
            strokeWidth={5}
          >
            <Label
              content={({ viewBox }) => {
                if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                  return (
                    <text
                      x={viewBox.cx}
                      y={viewBox.cy}
                      textAnchor="middle"
                      dominantBaseline="middle"
                    >
                      <tspan
                        x={viewBox.cx}
                        y={viewBox.cy}
                        className="fill-foreground text-3xl font-bold"
                      >
                        {completed.data}%
                      </tspan>
                      <tspan
                        x={viewBox.cx}
                        y={(viewBox.cy || 0) + 24}
                        className="fill-muted-foreground"
                      >
                        Progress Profil
                      </tspan>
                    </text>
                  )
                }
              }}
            />
          </Pie>
        </PieChart>
      </ChartContainer>
      <p className="text-center">
        Profil perlu dilengkapi sebelum melamar pekerjaan
      </p>
    </div>
  )
}
